import {
    ApolloClient,
    ApolloProvider,
    createHttpLink,
    from,
    InMemoryCache,
    ServerError,
} from '@apollo/client';
import React from 'react';
import { setContext } from '@apollo/client/link/context';
import { formatISO } from 'date-fns/fp';
import { onError } from '@apollo/client/link/error';
import { RoutesEnum } from '../types/routes.enum';
import { RetryLink } from '@apollo/client/link/retry';
import { HttpStatusCode } from '../types/http-status-code.enum';

interface Props {
    children: React.ReactNode;
}

export const cache = new InMemoryCache();
const httpLink = createHttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URL,
    credentials: 'include',
});

const errorLink = onError((error) => {
    const serverError = error.networkError as ServerError;
    if (serverError && serverError.statusCode && window) {
        if (serverError.statusCode === HttpStatusCode.UNAUTHORIZED) {
            window.location.href =
                window.location.origin +
                RoutesEnum.LOGOUT +
                '?redirectTo=' +
                window.location.pathname;
        }
    }
});

/**
 * Attempt an operation multiple times if it fails due to network or server errors.
 */
const retryLink = new RetryLink({
    attempts: {
        max: 3,
        retryIf: (error: ServerError, _operation) => {
            if (error.statusCode === HttpStatusCode.INTERNAL_SERVER_ERROR) {
                return !!error;
            }
            return false;
        },
    },
    delay: {
        initial: 1500,
        max: Infinity,
        jitter: true,
    },
});

const httpHeadersMiddlewareLink = setContext(
    async (_, { headers, ...context }) => {
        return {
            headers: {
                ...headers,
                ClientTime: formatISO(new Date()),
                clientIanaTimeZone: Intl.DateTimeFormat().resolvedOptions()
                    .timeZone,
            },
            ...context,
        };
    },
);

const client = new ApolloClient({
    link: from([retryLink, errorLink, httpHeadersMiddlewareLink, httpLink]),
    cache: cache,
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'ignore',
        },
        query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
        },
        mutate: {
            errorPolicy: 'all',
        },
    },
});
const ApolloProviderWrapper = ({ children }: Props) => {
    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloProviderWrapper;
